import { type Nullable } from '@Types';
import { formatCurrencyForDisplay } from 'Currency';
import { DISCUSS_OTHER_OPTIONS, type AdditionalFinancingOption } from 'CohortSectionOffer';

type FormattedAdditionalFinancingOption = {
    additionalFinancingOption: AdditionalFinancingOption;
    paymentInformation: {
        formattedPaymentAmountPerInterval: Nullable<string>;
    };
};

export function additionalFinancingOptionWithFormattedValues(
    tuitionAmount: number,
    additionalFinancingOption: AdditionalFinancingOption,
): FormattedAdditionalFinancingOption {
    const formattedPaymentAmountPerInterval =
        additionalFinancingOption.label === DISCUSS_OTHER_OPTIONS
            ? null
            : formatCurrencyForDisplay(calculatePaymentAmountPerInterval(tuitionAmount, additionalFinancingOption));

    return {
        additionalFinancingOption,
        paymentInformation: { formattedPaymentAmountPerInterval },
    };
}

/**
 * Calculates the monthly payment for an amortizing loan,
 * rounded up to the nearest dollar.
 *
 * @param tuitionAmount - The initial loan amount
 * @param additionalFinancingOption - Object containing APR and loan duration in months
 * @returns The rounded-up monthly payment amount
 */
export function calculatePaymentAmountPerInterval(
    tuitionAmount: number,
    additionalFinancingOption: AdditionalFinancingOption,
): number {
    const { apr, monthlyDuration: n } = additionalFinancingOption;
    const i = apr / 12; // monthly interest rate

    let monthlyPayment: number;

    if (i === 0) {
        monthlyPayment = tuitionAmount / n;
    } else {
        monthlyPayment = (tuitionAmount * (i * (1 + i) ** n)) / ((1 + i) ** n - 1);
    }

    return Math.ceil(monthlyPayment); // Round up to nearest dollar
}
